import { getReferenceString } from '@medplum/core';
import { Appointment, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useEffect, useState } from 'react';
import { appointmentTypeTokens, CONSULTATION_APPOINTMENT_CODES } from '@bariendo/lib/constants';

/**
 * The patient's upcoming live consult, when staff are booking a new one (BAR-2883).
 * Staff are warned, not blocked: a second consult is sometimes on purpose.
 */
export function useUpcomingConsult(
  patient: Patient | undefined,
  appointmentTypeCode: string | null,
  enabled: boolean
): Appointment | undefined {
  const medplum = useMedplum();
  const [consult, setConsult] = useState<Appointment>();
  const isConsult = (CONSULTATION_APPOINTMENT_CODES as readonly string[]).includes(appointmentTypeCode ?? '');
  const patientRef = patient?.id ? getReferenceString(patient) : undefined;

  useEffect(() => {
    setConsult(undefined);
    if (!enabled || !isConsult || !patientRef) {
      return;
    }
    let current = true;
    medplum
      .searchOne('Appointment', {
        patient: patientRef,
        'appointment-type': appointmentTypeTokens(CONSULTATION_APPOINTMENT_CODES).join(','),
        date: `ge${new Date().toISOString()}`,
        status: 'proposed,pending,booked,arrived,checked-in',
        _sort: 'date',
      })
      .then((found) => current && setConsult(found))
      .catch(() => current && setConsult(undefined));
    return () => {
      current = false;
    };
  }, [medplum, enabled, isConsult, patientRef]);

  return consult;
}
